import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'

interface IframeBubbleProps {
  readonly src: string
  readonly title?: string
  readonly height?: number
  readonly className?: string
  readonly onLoad?: () => void
  readonly onError?: () => void
  readonly allowFullscreen?: boolean
  readonly sandbox?: string
  readonly assistantMessage?: string
}

interface IframeLoadState {
  loading: boolean
  error: boolean
  loaded: boolean
}

const IframeBubble = forwardRef<HTMLIFrameElement, IframeBubbleProps>(function IframeBubble(
  {
    src,
    title = 'Embedded Content',
    height = 400,
    className,
    onLoad,
    onError,
    allowFullscreen = false,
    sandbox = 'allow-scripts allow-same-origin allow-forms allow-popups allow-downloads',
    assistantMessage,
  },
  ref
) {
  const [loadState, setLoadState] = useState<IframeLoadState>({
    loading: true,
    error: false,
    loaded: false,
  })
  const [dynamicHeight, setDynamicHeight] = useState<number>(height)
  const internalRef = useRef<HTMLIFrameElement>(null)

  useImperativeHandle(ref, () => internalRef.current!, [])

  const iframeKey = useMemo(() => `iframe-${src}`, [src])

  const containerClassNames = classNames({
    'pilot-iframe-bubble': true,
    'loading': loadState.loading,
    'error': loadState.error,
    'loaded': loadState.loaded,
    [className || '']: !!className,
  })

  const handleLoad = useCallback(() => {
    setLoadState({
      loading: false,
      error: false,
      loaded: true,
    })

    if (onLoad) {
      onLoad()
    }
  }, [onLoad])

  const handleError = useCallback(() => {
    setLoadState({
      loading: false,
      error: true,
      loaded: false,
    })
    if (onError) {
      onError()
    }
  }, [onError])

  useEffect(() => {
    if (assistantMessage && loadState.loaded && internalRef.current?.contentWindow) {
      setTimeout(() => {
        internalRef.current?.contentWindow?.postMessage(
          {
            type: 'assistant_message',
            message: assistantMessage,
          },
          '*'
        )
      }, 100)
    }
  }, [assistantMessage, loadState.loaded])

  useEffect(() => {
    const iframe = internalRef.current
    if (!iframe) return

    setLoadState({
      loading: true,
      error: false,
      loaded: false,
    })

    setDynamicHeight(height)

    const loadingTimeout = setTimeout(() => {
      setLoadState({
        loading: false,
        error: false,
        loaded: true,
      })
    }, 800)

    const handleLoadWithCleanup = () => {
      clearTimeout(loadingTimeout)
      handleLoad()
    }

    const handleErrorWithCleanup = () => {
      clearTimeout(loadingTimeout)
      handleError()
    }

    iframe.addEventListener('load', handleLoadWithCleanup)
    iframe.addEventListener('error', handleErrorWithCleanup)

    const handleIframeMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) {
        return
      }

      const { type, height: newHeight, url, target } = event.data

      if (type === 'iframe_height_change' && newHeight && typeof newHeight === 'number') {
        const adjustedHeight = Math.min(Math.max(newHeight, 200), 800)
        setDynamicHeight(adjustedHeight)

        if (iframe) {
          iframe.style.height = `${adjustedHeight}px`
        }
      }

      if (type === 'open_url' && url) {
        window.open(url, target || '_blank')
      }
    }

    window.addEventListener('message', handleIframeMessage)

    return () => {
      clearTimeout(loadingTimeout)
      iframe.removeEventListener('load', handleLoadWithCleanup)
      iframe.removeEventListener('error', handleErrorWithCleanup)
      window.removeEventListener('message', handleIframeMessage)
    }
  }, [src, height, handleLoad, handleError])

  const renderLoadingState = () => (
    <div className='pilot-iframe-bubble__loading'>
      <FontAwesomeIcon icon={faSpinner} className='fa-spin' />
      <span className='ms-2'>Loading...</span>
    </div>
  )

  const renderErrorState = () => (
    <div className='pilot-iframe-bubble__error'>
      <FontAwesomeIcon icon={faExclamationTriangle} />
      <span className='ms-2'>Failed to load content</span>
    </div>
  )

  return (
    <div className={containerClassNames}>
      {loadState.loading && renderLoadingState()}
      {loadState.error && renderErrorState()}

      <iframe
        key={iframeKey}
        ref={internalRef}
        src={src}
        title={title}
        width='100%'
        height={dynamicHeight}
        allowFullScreen={allowFullscreen}
        sandbox={sandbox}
        style={{
          display: loadState.loading || loadState.error ? 'none' : 'block',
          border: 'none',
          borderRadius: '8px',
          maxWidth: '100%',
          transition: 'height 0.3s ease',
          overflow: 'hidden',
        }}
        aria-label={title}
      />
    </div>
  )
})

export default IframeBubble
